import { useState } from "react";
import { Award, MessageSquareQuote } from "lucide-react";
import AdminContent from "./AdminContent";

const PLACEMENT_FIELDS = [
  { k: "studentName", label: "Student Full Name" },
  { k: "course", label: "Course Completed" },
  { k: "company", label: "Company Name" },
  { k: "designation", label: "Job Designation" },
  { k: "placementDate", label: "Placement Year / Date" },
  { k: "photo", label: "Student Photograph", type: "file", accept: "image/*" },
  { k: "document", label: "Placement Confirmation Document (optional)", type: "file", accept: "image/*,application/pdf", media: "doc" },
  { k: "studentSlug", label: "Link Key (optional — set the SAME key on this student's testimonial to show a 'Watch Student Experience' button, e.g. deepanshu-sharma)" },
  { k: "order", label: "Display Order (optional, lower shows first)", type: "number" },
];

const TESTIMONIAL_FIELDS = [
  { k: "studentName", label: "Student Name" },
  { k: "course", label: "Course" },
  { k: "feedback", label: "Written Feedback (optional)", type: "textarea", rows: 4 },
  { k: "youtubeUrl", label: "YouTube Testimonial Link (optional)" },
  { k: "videoFile", label: "Upload Video Testimonial (optional)", type: "file", accept: "video/*", media: "video" },
  { k: "photo", label: "Student Photograph (optional)", type: "file", accept: "image/*" },
  { k: "studentSlug", label: "Link Key (optional — match a placement's key to connect them)" },
  { k: "order", label: "Display Order (optional, lower shows first)", type: "number" },
];

const TABS = [
  { key: "placements", label: "Placement Results", icon: Award },
  { key: "testimonials", label: "Student Testimonials", icon: MessageSquareQuote },
];

export default function AdminResults() {
  const [tab, setTab] = useState("placements");

  return (
    <div data-testid="admin-results">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Results Management</h1>
        <p className="text-sm text-slate-500">
          Manage student placement results and testimonials shown on the public &quot;Our Results&quot; page.
          Only publish information ASAI is authorized to share — never fake data.
        </p>
      </div>

      <div className="mb-6 inline-flex rounded-xl border border-slate-200 bg-white p-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`results-tab-${t.key}`}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                active ? "bg-orange text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "placements" ? (
        <AdminContent
          kind="placements"
          title="Placement Results"
          note="Add verified student placements. Upload the student's photo and (optionally) a placement confirmation document."
          fields={PLACEMENT_FIELDS}
          toggle={{ field: "published", on: "Published", off: "Hidden" }}
          display={(it) => it.studentName || "Student"}
        />
      ) : (
        <AdminContent
          kind="resultTestimonials"
          title="Student Testimonials"
          note="Add genuine student testimonials — written feedback, a YouTube link, or an uploaded video. Do not publish artificial reviews."
          fields={TESTIMONIAL_FIELDS}
          toggle={{ field: "published", on: "Published", off: "Hidden" }}
          display={(it) => it.studentName || "Student"}
        />
      )}
    </div>
  );
}
